/**
 * ONBOARDING GATE (LOGIN-TO-TENANT.md §3) — signed in but in NO workspace yet → show the resolver's screen instead of
 * the shell (which would otherwise call tenant-scoped APIs with no tenant: the 403 storm).
 *
 *   invited      → "You're invited to X — Join" (the existing /join accept flow)
 *   create       → "Create your workspace" (consumer sign-up; the Stripe gate follows for its OWNER)
 *   invite_only  → "You need an invitation" (enterprise deployment) + sign out
 *   unavailable  → no verdict (the call failed) → Retry — never "ask for an invite" to a real member
 *
 * With an active workspace it renders its children unchanged.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext.jsx';

export function onboardingScreen({ activeTenantId, onboarding, pendingInvites }) {
  if (activeTenantId) return null;
  if (!onboarding) {
    // no verdict (the workspaces call failed / an older server): never tell a real member "you need an invitation"
    return (pendingInvites || []).length ? 'invited' : 'unavailable';
  }
  // "ready" without an active workspace = its session couldn't be opened → retry, not "ask for an invite"
  return onboarding.state === 'ready' ? 'unavailable' : onboarding.state;
}

function Card({ title, children }) {
  return (
    <div data-testid="onboarding-gate" className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="mb-2 text-lg font-semibold text-gray-900">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export default function OnboardingGate({ children }) {
  const { user, activeTenantId, onboarding, pendingInvites, createWorkspace, signOut } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(() => {
    const first = String(user?.name || user?.email || '').split(/[@\s]/)[0];
    return first ? `${first}'s workspace` : 'My workspace';
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const screen = onboardingScreen({ activeTenantId, onboarding, pendingInvites });
  if (!screen) return children;

  if (screen === 'invited') {
    return (
      <Card title="You're invited">
        <p className="mb-4 text-sm text-gray-600">Join a workspace to get started.</p>
        <ul className="space-y-2">
          {(pendingInvites || []).map((inv) => (
            <li key={inv.token} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
              <span className="text-sm text-gray-800">
                <span className="font-medium">{inv.name || inv.tenantId}</span>
                <span className="ml-2 text-xs text-gray-500">as {String(inv.role || 'USER').toLowerCase()}</span>
              </span>
              <button type="button" data-testid={`onboarding-join-${inv.tenantId}`}
                onClick={() => navigate(`/join?token=${encodeURIComponent(inv.token)}`)}
                className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700">
                Join
              </button>
            </li>
          ))}
        </ul>
        {onboarding?.canCreate ? (
          <p className="mt-4 text-xs text-gray-500">Or create your own workspace after joining.</p>
        ) : null}
      </Card>
    );
  }

  if (screen === 'create') {
    const submit = async (e) => {
      e.preventDefault();
      if (!name.trim() || busy) return;
      setBusy(true);
      setError('');
      try {
        await createWorkspace(name.trim());
      } catch (err) {
        setError((err && err.message) || "Couldn't create the workspace — try again.");
      } finally {
        setBusy(false);
      }
    };
    return (
      <Card title="Create your workspace">
        <p className="mb-4 text-sm text-gray-600">Your workspace is where your networks and rooms live. You can invite
          others to it later.</p>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="onboarding-create-name" value={name} onChange={(e) => setName(e.target.value)}
            maxLength={120} aria-label="Workspace name"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none" />
          {error ? <p className="text-xs text-red-600">{error}</p> : null}
          <button type="submit" data-testid="onboarding-create" disabled={busy || !name.trim()}
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {busy ? 'Creating…' : 'Create workspace'}
          </button>
        </form>
      </Card>
    );
  }

  if (screen === 'unavailable') {
    return (
      <Card title="Couldn't open your workspace">
        <p data-testid="onboarding-unavailable" className="mb-4 text-sm text-gray-600">
          We couldn't load your workspaces just now. Try again in a moment.
        </p>
        <button type="button" onClick={() => window.location.reload()}   /* re-runs the whole sign-in bootstrap (session included) */
          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700">
          Retry
        </button>
      </Card>
    );
  }

  return (
    <Card title="You need an invitation">
      <p data-testid="onboarding-invite-only" className="mb-4 text-sm text-gray-600">
        You're signed in as <span className="font-medium">{user?.email || 'you'}</span>, but you're not in a workspace
        yet. Ask your administrator to invite this email address — the invitation will appear here when you sign in.
      </p>
      <button type="button" onClick={() => signOut && signOut()}
        className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors border border-gray-300 text-gray-700 hover:bg-gray-50">
        Sign out
      </button>
    </Card>
  );
}
